if (!customElements.get('multicolumn-review-component')) {
  class MulticolumnReviewComponent extends HTMLElement {
    constructor() {
      super();

      this.slider = this.querySelector('[id^="Slider-"]');
      this.slides = Array.from(this.querySelectorAll('[id^="Slide-"]'));
      this.cards = Array.from(this.querySelectorAll('.multicolumn-card'));
      this.slideDots = Array.from(
        this.querySelectorAll('.multicolumn-review__dot:not(.professional-endorsements__page-dot)')
      );
      this.pageDots = Array.from(this.querySelectorAll('.professional-endorsements__page-dot'));
      this.prevButton = this.querySelector('.slider-button--prev');
      this.nextButton = this.querySelector('.slider-button--next');
      this.currentPage = this.querySelector('.slider-counter--current');
      this.totalPages = this.querySelector('.slider-counter--total');
      this.progressBar = this.querySelector('.professional-endorsements__progress-track');
      this.mobileMedia = window.matchMedia('(max-width: 749px)');
      this.matchCardHeightsMobile = this.dataset.matchCardHeightsMobile === 'true';
      this.desktopCarousel = this.dataset.desktopCarousel === 'true';
      this.carouselStep = parseInt(this.dataset.carouselStep, 10) || 1;
      this.currentIndex = 0;
      this.flickity = null;
      this.scrollFrame = null;
      this.scrollAnimationFrame = null;
      this.scrollAnimationTarget = null;
      this.scrollDuration = parseInt(this.dataset.scrollDuration, 10) || 720;
      this.resizeFrame = null;
      this.programmaticScrollTimer = null;
      this.focusableSelector = 'a[href], button, input, select, textarea, summary, [tabindex]:not([tabindex="-1"])';
      this.boundDotHandlers = [];
      this.boundMediaChange = this.handleViewportChange.bind(this);
      this.boundPrevClick = this.handlePreviousClick.bind(this);
      this.boundNextClick = this.handleNextClick.bind(this);
      this.boundScroll = this.handleScroll.bind(this);
      this.boundResize = this.scheduleSyncCardHeights.bind(this);
      this.boundMediaLoad = this.scheduleSyncCardHeights.bind(this);
    }

    connectedCallback() {
      if (!this.slider || this.slides.length === 0) return;

      this.mediaNodes = Array.from(this.querySelectorAll('img, video'));

      if (this.totalPages) {
        this.totalPages.textContent = String(this.getTotalPageCount());
      }

      if (this.progressBar) {
        const totalPages = this.getTotalPageCount();
        const currentPage = this.getCurrentPageNumber(0);
        this.progressBar.setAttribute('aria-valuemax', String(totalPages));
        this.progressBar.setAttribute('aria-valuenow', String(currentPage));
        this.style.setProperty('--professional-endorsements-progress', String(currentPage / totalPages));
      }

      this.bindDots();
      this.prevButton && this.prevButton.addEventListener('click', this.boundPrevClick);
      this.nextButton && this.nextButton.addEventListener('click', this.boundNextClick);

      if (this.mobileMedia.addEventListener) {
        this.mobileMedia.addEventListener('change', this.boundMediaChange);
      } else {
        this.mobileMedia.addListener(this.boundMediaChange);
      }

      window.addEventListener('resize', this.boundResize);

      this.mediaNodes.forEach((node) => {
        const eventName = node.tagName === 'VIDEO' ? 'loadeddata' : 'load';
        node.addEventListener(eventName, this.boundMediaLoad);
      });

      this.updateState(0);
      this.handleViewportChange();
      this.scheduleSyncCardHeights();
    }

    disconnectedCallback() {
      this.destroySlider();
      this.disableScrollFallback();

      this.boundDotHandlers.forEach(({ dot, handler }) => {
        dot.removeEventListener('click', handler);
      });
      this.boundDotHandlers = [];

      this.prevButton && this.prevButton.removeEventListener('click', this.boundPrevClick);
      this.nextButton && this.nextButton.removeEventListener('click', this.boundNextClick);

      if (this.mobileMedia.removeEventListener) {
        this.mobileMedia.removeEventListener('change', this.boundMediaChange);
      } else {
        this.mobileMedia.removeListener(this.boundMediaChange);
      }

      window.removeEventListener('resize', this.boundResize);

      if (this.mediaNodes) {
        this.mediaNodes.forEach((node) => {
          const eventName = node.tagName === 'VIDEO' ? 'loadeddata' : 'load';
          node.removeEventListener(eventName, this.boundMediaLoad);
        });
      }

      if (this.resizeFrame) {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = null;
      }

      if (this.programmaticScrollTimer) {
        clearTimeout(this.programmaticScrollTimer);
        this.programmaticScrollTimer = null;
      }
    }

    bindDots() {
      this.slideDots.forEach((dot, index) => {
        const handler = () => this.goToIndex(index);
        this.boundDotHandlers.push({ dot, handler });
        dot.addEventListener('click', handler);
      });

      this.pageDots.forEach((dot, pageIndex) => {
        const handler = () => this.goToPage(pageIndex);
        this.boundDotHandlers.push({ dot, handler });
        dot.addEventListener('click', handler);
      });
    }

    handleViewportChange() {
      const shouldUseSlider =
        this.mobileMedia.matches && this.slides.length > 1 && typeof window.Flickity !== 'undefined';

      if (shouldUseSlider) {
        this.disableScrollFallback();
        this.initSlider();
      } else {
        this.destroySlider();

        if (this.mobileMedia.matches) {
          this.enableScrollFallback();
        } else if (this.desktopCarousel && this.slides.length > 1) {
          this.enableScrollFallback();
        } else {
          this.disableScrollFallback();
        }

        this.updateState(this.getClosestSlideIndex());
      }

      this.scheduleSyncCardHeights();
    }

    initSlider() {
      if (this.flickity || !this.slider) return;

      this.classList.add('is-mobile-slider');
      this.flickity = new window.Flickity(this.slider, {
        accessibility: true,
        adaptiveHeight: !this.matchCardHeightsMobile,
        rightToLeft: window.theme && window.theme.config ? window.theme.config.rtl : false,
        prevNextButtons: false,
        pageDots: false,
        wrapAround: true,
        draggable: true,
        cellAlign: 'center',
        contain: false,
        selectedAttraction: 0.1,
        friction: 0.85,
      });

      this.flickity.on('ready', () => {
        this.currentIndex = this.flickity.selectedIndex || 0;
        this.updateState(this.currentIndex);
        this.scheduleSyncCardHeights();
      });

      this.flickity.on('change', (index) => {
        this.currentIndex = index;
        this.updateState(index);
      });

      this.flickity.on('settle', () => {
        this.currentIndex = this.flickity.selectedIndex || 0;
        this.updateState(this.currentIndex);
        this.scheduleSyncCardHeights();
      });
    }

    destroySlider() {
      if (!this.flickity) {
        this.classList.remove('is-mobile-slider');
        return;
      }

      this.flickity.destroy();
      this.flickity = null;
      this.classList.remove('is-mobile-slider');
      this.slides.forEach((slide) => slide.classList.remove('is-selected', 'is-next', 'is-previous'));
    }

    enableScrollFallback() {
      this.slider.removeEventListener('scroll', this.boundScroll);
      this.slider.addEventListener('scroll', this.boundScroll, { passive: true });
    }

    disableScrollFallback() {
      this.slider.removeEventListener('scroll', this.boundScroll);
      if (this.scrollFrame) {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }

      if (this.scrollAnimationFrame) {
        cancelAnimationFrame(this.scrollAnimationFrame);
        this.scrollAnimationFrame = null;
      }
    }

    handleScroll() {
      if (this.flickity) return;
      if (this.programmaticScrollTimer) return;

      if (this.scrollFrame) {
        cancelAnimationFrame(this.scrollFrame);
      }

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.currentIndex = this.getClosestSlideIndex();
        this.updateState(this.currentIndex);
      });
    }

    handlePreviousClick(event) {
      event.preventDefault();

      if (this.flickity) {
        this.flickity.previous();
        return;
      }

      if (this.isDesktopCarouselActive()) {
        this.goToPage(Math.max(0, this.getPageIndex(this.currentIndex) - 1));
        return;
      }

      this.goToIndex(Math.max(0, this.currentIndex - 1));
    }

    handleNextClick(event) {
      event.preventDefault();

      if (this.flickity) {
        this.flickity.next();
        return;
      }

      if (this.isDesktopCarouselActive()) {
        this.goToPage(Math.min(this.getTotalPageCount() - 1, this.getPageIndex(this.currentIndex) + 1));
        return;
      }

      this.goToIndex(Math.min(this.slides.length - 1, this.currentIndex + 1));
    }

    getTotalPageCount() {
      if (this.isDesktopCarouselActive()) {
        return Math.max(1, Math.ceil(this.slides.length / this.carouselStep));
      }

      return this.slides.length;
    }

    getCurrentPageNumber(activeIndex) {
      if (this.isDesktopCarouselActive()) {
        return this.getPageIndex(activeIndex) + 1;
      }

      return activeIndex + 1;
    }

    isDesktopCarouselActive() {
      return this.desktopCarousel && !this.mobileMedia.matches;
    }

    getPageIndex(activeIndex) {
      const totalPages = this.getTotalPageCount();
      if (totalPages <= 1) return 0;

      return Math.min(totalPages - 1, Math.floor(activeIndex / this.carouselStep));
    }

    getPageStartIndex(pageIndex) {
      const totalPages = this.getTotalPageCount();
      if (totalPages <= 1) return 0;

      const clampedPage = Math.max(0, Math.min(totalPages - 1, pageIndex));
      return Math.min(this.slides.length - 1, clampedPage * this.carouselStep);
    }

    updateDesktopControls(activeIndex) {
      if (!this.isDesktopCarouselActive() || this.flickity) {
        this.classList.remove('slider--no-buttons');
        return;
      }

      const totalPages = this.getTotalPageCount();

      if (totalPages <= 1) {
        this.classList.add('slider--no-buttons');
        this.prevButton && this.prevButton.setAttribute('disabled', 'disabled');
        this.nextButton && this.nextButton.setAttribute('disabled', 'disabled');
        return;
      }

      this.classList.remove('slider--no-buttons');

      const currentPage = this.getPageIndex(activeIndex);
      const isFirstPage = currentPage === 0;
      const isLastPage = currentPage === totalPages - 1;

      if (this.prevButton) {
        if (isFirstPage) {
          this.prevButton.setAttribute('disabled', 'disabled');
        } else {
          this.prevButton.removeAttribute('disabled');
        }
      }

      if (this.nextButton) {
        if (isLastPage) {
          this.nextButton.setAttribute('disabled', 'disabled');
        } else {
          this.nextButton.removeAttribute('disabled');
        }
      }
    }

    goToPage(pageIndex) {
      this.goToIndex(this.getPageStartIndex(pageIndex));
    }

    goToIndex(index) {
      if (index < 0 || index >= this.slides.length) return;

      if (this.flickity) {
        this.flickity.select(index);
        return;
      }

      const targetSlide = this.slides[index];
      if (!targetSlide) return;

      this.currentIndex = index;
      this.updateState(index);
      this.suspendScrollSync();
      this.animateScrollTo(targetSlide.offsetLeft);
    }

    animateScrollTo(targetLeft) {
      if (!this.slider) return;

      if (this.scrollAnimationFrame) {
        cancelAnimationFrame(this.scrollAnimationFrame);
        this.scrollAnimationFrame = null;
      }

      const startLeft = this.slider.scrollLeft;
      const distance = targetLeft - startLeft;

      if (distance === 0) return;

      const duration = this.scrollDuration;
      const startTime = performance.now();
      this.scrollAnimationTarget = targetLeft;

      const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

      const step = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(1, elapsed / duration);
        const eased = easeInOutCubic(progress);

        this.slider.scrollLeft = startLeft + distance * eased;

        if (progress < 1) {
          this.scrollAnimationFrame = requestAnimationFrame(step);
        } else {
          this.scrollAnimationFrame = null;
          this.scrollAnimationTarget = null;
        }
      };

      this.scrollAnimationFrame = requestAnimationFrame(step);
    }

    suspendScrollSync() {
      if (!this.desktopCarousel || this.mobileMedia.matches) return;

      if (this.programmaticScrollTimer) {
        clearTimeout(this.programmaticScrollTimer);
      }

      this.programmaticScrollTimer = setTimeout(() => {
        this.programmaticScrollTimer = null;
      }, this.scrollDuration + 120);
    }

    updateState(activeIndex) {
      if (activeIndex < 0 || activeIndex >= this.slides.length) return;

      this.currentIndex = activeIndex;

      this.slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.classList.toggle('is-selected', isActive);
        this.updateSlideAccessibility(slide, isActive);
      });

      this.slideDots.forEach((dot, index) => {
        const isActive = this.mobileMedia.matches && index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      this.pageDots.forEach((dot, pageIndex) => {
        const activePage = this.getPageIndex(activeIndex);
        const isActive = !this.mobileMedia.matches && pageIndex === activePage;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      if (this.currentPage) {
        this.currentPage.textContent = String(this.getCurrentPageNumber(activeIndex));
      }

      if (this.totalPages) {
        this.totalPages.textContent = String(this.getTotalPageCount());
      }

      if (this.progressBar) {
        const totalPages = this.getTotalPageCount();
        const currentPage = this.getCurrentPageNumber(activeIndex);
        this.progressBar.setAttribute('aria-valuemax', String(totalPages));
        this.progressBar.setAttribute('aria-valuenow', String(currentPage));
        this.style.setProperty('--professional-endorsements-progress', String(currentPage / totalPages));
      }

      this.updateDesktopControls(activeIndex);
    }

    updateSlideAccessibility(slide, isActive) {
      const shouldPruneFocus = this.mobileMedia.matches && this.slides.length > 1;

      if (!shouldPruneFocus) {
        slide.removeAttribute('aria-hidden');
        this.restoreSlideFocusables(slide);
        return;
      }

      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      if (isActive) {
        this.restoreSlideFocusables(slide);
      } else {
        this.disableSlideFocusables(slide);
      }
    }

    disableSlideFocusables(slide) {
      slide.querySelectorAll(this.focusableSelector).forEach((element) => {
        if (!element.hasAttribute('data-original-tabindex')) {
          element.setAttribute('data-original-tabindex', element.getAttribute('tabindex') || '');
        }
        element.setAttribute('tabindex', '-1');
      });
    }

    restoreSlideFocusables(slide) {
      slide.querySelectorAll('[data-original-tabindex]').forEach((element) => {
        const originalTabindex = element.getAttribute('data-original-tabindex');
        if (originalTabindex === '') {
          element.removeAttribute('tabindex');
        } else {
          element.setAttribute('tabindex', originalTabindex);
        }
        element.removeAttribute('data-original-tabindex');
      });
    }

    getClosestSlideIndex() {
      const scrollLeft = this.slider.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (this.desktopCarousel && !this.mobileMedia.matches) {
        const pageIndex = Math.round(closestIndex / this.carouselStep);
        return Math.min(this.slides.length - 1, pageIndex * this.carouselStep);
      }

      return closestIndex;
    }

    scheduleSyncCardHeights() {
      if (this.resizeFrame) {
        cancelAnimationFrame(this.resizeFrame);
      }

      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.syncCardHeights();
      });
    }

    syncCardHeights() {
      this.style.removeProperty('--multicolumn-review-equal-card-height');

      if (!this.matchCardHeightsMobile || !this.mobileMedia.matches || this.cards.length === 0) {
        return;
      }

      let maxHeight = 0;

      this.cards.forEach((card) => {
        maxHeight = Math.max(maxHeight, card.offsetHeight);
      });

      if (maxHeight > 0) {
        this.style.setProperty('--multicolumn-review-equal-card-height', `${maxHeight}px`);
      }
    }
  }

  customElements.define('multicolumn-review-component', MulticolumnReviewComponent);
}
